import { matchPath } from 'react-router-dom';
import type { IconName } from '../components/icons';
import { hatRecht, type Recht, type RechteTraeger } from '../berechtigungen';

/**
 * Einzige Quelle fuer alle Routen der App. Aus dieser Liste speisen sich
 * Sidebar, Befehlspalette (Ctrl+K), Brotkrumen, Dokumenttitel und die
 * Routen-Definition in App.tsx (ueber routeElements.tsx). Ein Eintrag ohne
 * `group` ist eine Detail- oder versteckte Seite und taucht nur in den
 * Brotkrumen auf.
 */
export type NavGroupId = 'start' | 'linkedin' | 'content' | 'sales' | 'sales-tools' | 'erp' | 'admin';

export interface NavGroup {
  id: NavGroupId;
  /** null = Gruppe ohne Ueberschrift (Start). */
  label: string | null;
  collapsible: boolean;
}

export interface RouteEntry {
  /** Stabile Kennung (Palette, Brotkrumen, Tests). */
  id: string;
  /** react-router-Muster, z. B. '/companies/:id'. */
  path: string;
  label: string;
  /** Kurzform fuer Rail-Tooltip und Brotkrumen. */
  shortLabel?: string;
  icon?: IconName;
  /** Fehlt sie, ist der Eintrag nicht in der Sidebar. */
  group?: NavGroupId;
  /** Hinweistext in der Befehlspalette. */
  description?: string;
  /** Zusaetzliche Suchbegriffe fuer die Palette. */
  keywords?: string[];
  /** Beschriftung in der Seitenleiste, falls sie vom Seitentitel abweicht (Phase, Sammelseite). */
  navLabel?: string;
  /** id des Elterneintrags fuer die Brotkrumen-Kette. */
  parent?: string;
  /** id des Sidebar-Eintrags, unter dem diese Seite als Reiter erscheint (selbst ohne `group`). */
  tabOf?: string;
  /** Schritt-Kennung im LinkedIn-Prozess (siehe src/linkedin/flowSteps.ts). */
  flowStep?: string;
  /**
   * Recht, das die Seite sichtbar macht (Kapitel 4.4 des Plans). Ohne
   * `recht` sieht sie jede angemeldete Person (Start, Profil). Mit `recht`
   * fehlt sie ohne dieses Recht in Sidebar und Palette, und AppLayout zeigt
   * statt der Seite "Kein Zugriff" -- auch fuer Lesezeichen.
   */
  recht?: Recht;
}

export const NAV_GROUPS: NavGroup[] = [
  { id: 'start', label: null, collapsible: false },
  { id: 'linkedin', label: 'LinkedIn-Studio', collapsible: true },
  { id: 'content', label: 'Inhalte & Werkzeuge', collapsible: true },
  { id: 'sales', label: 'Vertrieb', collapsible: true },
  { id: 'sales-tools', label: 'Vertrieb: Vorbereitung', collapsible: true },
  { id: 'erp', label: 'ERP', collapsible: true },
  { id: 'admin', label: 'Verwaltung', collapsible: true },
];

export const PUBLIC_PATHS = ['/login', '/oidc/callback'];

export const ROUTES: RouteEntry[] = [
  // --- Start ---------------------------------------------------------------
  {
    id: 'home', path: '/', label: 'Start', icon: 'home', group: 'start',
    description: 'Überblick: LinkedIn-Fortschritt, Vertrieb heute, Schnellzugriff', keywords: ['start', 'home', 'übersicht', 'dashboard'],
  },

  // --- LinkedIn-Studio: fuenf Phasen, die Schritte darin als Reiter --------
  {
    id: 'li-overview', recht: 'linkedin.lesen', path: '/gesamtprozess', label: 'Übersicht & Fortschritt', shortLabel: 'LinkedIn-Studio',
    icon: 'linkedin', group: 'linkedin', description: 'Der LinkedIn-Prozess auf einen Blick',
    keywords: ['gesamtprozess', 'linkedin', 'prozess', 'fortschritt'],
  },
  // Phase 1: Grundlagen
  {
    id: 'li-strategy', recht: 'linkedin.lesen', path: '/strategy', label: 'Strategie & Zielkundenprofil (ICP)', navLabel: 'Grundlagen', icon: 'compass', group: 'linkedin',
    flowStep: 'strategy', description: 'Zielgruppe, ICP und Pain Points', keywords: ['strategie', 'zielgruppe', 'icp', 'grundlagen', 'strategy', 'zielkundenprofil'],
  },
  {
    id: 'li-profiles', recht: 'linkedin.lesen', path: '/linkedin-profiles', label: 'Referenzprofile', icon: 'users', parent: 'li-strategy', tabOf: 'li-strategy',
    flowStep: 'profiles', description: 'Stilvorbilder und Beispiel-Posts', keywords: ['profile', 'referenz', 'vorbilder'],
  },
  // Phase 2: Formate & Ideen
  {
    id: 'li-series', recht: 'linkedin.lesen', path: '/series-formats', label: 'Serienformate', navLabel: 'Formate & Ideen', icon: 'layers', group: 'linkedin',
    flowStep: 'series', description: 'Wiederkehrende Beitragsformate', keywords: ['serie', 'format'],
  },
  {
    id: 'li-series-workflow', recht: 'linkedin.lesen', path: '/series-formats/:id/workflow', label: 'Serien-Workflow', parent: 'li-series',
  },
  {
    id: 'li-ideas', recht: 'linkedin.lesen', path: '/post-ideas', label: 'Beitragsideen', icon: 'lightbulb', parent: 'li-series', tabOf: 'li-series',
    flowStep: 'ideas', description: 'Ideen je Serienformat sammeln', keywords: ['ideen', 'beitrag', 'post'],
  },
  // Phase 3: Beitraege
  {
    id: 'li-briefs', recht: 'linkedin.lesen', path: '/content-briefs', label: 'Beitragsentwürfe (Content Briefs)', navLabel: 'Beiträge', icon: 'file-text', group: 'linkedin',
    flowStep: 'briefs', description: 'Thema, Herausforderung, Wendepunkt, Learning', keywords: ['brief', 'content', 'beiträge', 'content briefs', 'entwurf'],
  },
  {
    id: 'li-post', recht: 'linkedin.lesen', path: '/linkedin-post-generator', label: 'Beitrag erstellen', icon: 'edit', parent: 'li-briefs', tabOf: 'li-briefs',
    flowStep: 'post', description: 'Fertige LinkedIn-Beiträge aus Briefs', keywords: ['beitrag', 'generator', 'post', 'hook'],
  },
  {
    id: 'li-media', recht: 'linkedin.lesen', path: '/media', label: 'Medienbibliothek', shortLabel: 'Medien', icon: 'archive',
    parent: 'li-briefs', tabOf: 'li-briefs', description: 'Videos, PDF-Karussells und Bilder des ganzen Mandanten',
    keywords: ['medien', 'video', 'pdf', 'karussell', 'bilder', 'upload', 'media library', 'mediathek'],
  },
  // Phase 4: Planen & Veroeffentlichen
  {
    id: 'li-plan', recht: 'linkedin.lesen', path: '/editorial-plan', label: 'Wochenplan (Slots)', navLabel: 'Planen & Veröffentlichen', shortLabel: 'Wochenplan', icon: 'calendar', group: 'linkedin',
    flowStep: 'plan', description: 'Wochenplan mit Slots', keywords: ['redaktion', 'plan', 'kalender', 'veröffentlichen', 'redaktionsplan', 'editorial plan', 'slots'],
  },
  {
    id: 'li-calendar', recht: 'linkedin.lesen', path: '/publication-calendar', label: 'Terminieren', shortLabel: 'Terminieren',
    icon: 'clock', parent: 'li-plan', tabOf: 'li-plan', flowStep: 'calendar', description: 'Beiträge terminieren und freigeben',
    keywords: ['kalender', 'veroeffentlichung', 'termin', 'veröffentlichungskalender', 'publication calendar'],
  },
  {
    id: 'li-scheduled', recht: 'linkedin.lesen', path: '/scheduled-posts', label: 'Freigabe & Versand', shortLabel: 'Freigabe & Versand',
    icon: 'send', parent: 'li-plan', tabOf: 'li-plan', description: 'Freigabe und automatisches Posten', keywords: ['geplant', 'freigabe', 'posten', 'geplante posts', 'scheduled posts'],
  },
  // Phase 5: Messen
  {
    id: 'li-stats', recht: 'linkedin.lesen', path: '/linkedin-statistics', label: 'Statistiken & Konto', navLabel: 'Messen & Konto', shortLabel: 'Statistiken',
    icon: 'bar-chart', group: 'linkedin', flowStep: 'measure', description: 'LinkedIn-Konto und Kennzahlen',
    keywords: ['statistik', 'metriken', 'konto', 'verbinden', 'messen'],
  },

  // --- Inhalte & Werkzeuge -------------------------------------------------
  {
    id: 'text', recht: 'inhalte.lesen', path: '/text-generator', label: 'Text generieren', icon: 'sparkles', group: 'content',
    description: 'Freien Text mit KI erzeugen', keywords: ['ki', 'generieren', 'text', 'anzeige'],
  },
  {
    id: 'templates', recht: 'inhalte.lesen', path: '/templates', label: 'Vorlagen', icon: 'template', group: 'content',
    description: 'Prompt-Vorlagen durchsuchen', keywords: ['vorlagen', 'prompt', 'templates'],
  },
  // Workflows: eigene Ablaeufe plus die beiden festen Ablaeufe als Reiter
  {
    id: 'workflows', recht: 'inhalte.lesen', path: '/workflows', label: 'Meine Workflows', navLabel: 'Workflows', icon: 'workflow', group: 'content',
    description: 'Eigene mehrstufige KI-Workflows', keywords: ['workflow', 'automatisierung'],
  },
  { id: 'workflows-create', recht: 'inhalte.lesen', path: '/workflows/create', label: 'Neuer Workflow', parent: 'workflows' },
  { id: 'workflows-template', recht: 'inhalte.lesen', path: '/workflows/execute-template', label: 'Vorlage ausführen', parent: 'workflows' },
  { id: 'workflows-execute', recht: 'inhalte.lesen', path: '/workflows/:id/execute', label: 'Workflow ausführen', parent: 'workflows' },
  { id: 'workflows-edit', recht: 'inhalte.lesen', path: '/workflows/:id/edit', label: 'Workflow bearbeiten', parent: 'workflows' },
  {
    id: 'email', recht: 'inhalte.lesen', path: '/email-workflow', label: 'E-Mail erstellen', icon: 'mail', parent: 'workflows', tabOf: 'workflows',
    description: 'E-Mail-Workflow', keywords: ['email', 'mail', 'newsletter'],
  },
  {
    id: 'blog', recht: 'inhalte.lesen', path: '/blog-workflow', label: 'Blog erstellen', icon: 'book', parent: 'workflows', tabOf: 'workflows',
    description: 'Blog-Workflow', keywords: ['blog', 'artikel'],
  },
  // Medien: Flyer, Memes, Slides und Lead Magnets als Reiter einer Seite
  {
    id: 'li-flyer-new', recht: 'inhalte.lesen', path: '/flyer-generator', label: 'Flyer erzeugen', navLabel: 'Medien', icon: 'image', group: 'content',
    description: 'Flyer aus Beitragsideen rendern', keywords: ['flyer', 'bild', 'medien'],
  },
  {
    id: 'li-flyers', recht: 'inhalte.lesen', path: '/flyers', label: 'Flyer-Historie', icon: 'archive', parent: 'li-flyer-new', tabOf: 'li-flyer-new',
    description: 'Erzeugte Flyer herunterladen', keywords: ['flyer', 'historie'],
  },
  {
    id: 'memes', recht: 'inhalte.lesen', path: '/meme-generator', label: 'Meme-Generator', icon: 'smile', parent: 'li-flyer-new', tabOf: 'li-flyer-new',
    description: 'Memes mit KI-Text', keywords: ['meme', 'bild', 'meme generator'],
  },
  {
    id: 'slide-deck', recht: 'inhalte.lesen', path: '/slide-deck', label: 'Präsentationen (Slide-Deck)', shortLabel: 'Slide-Deck', icon: 'play', parent: 'li-flyer-new', tabOf: 'li-flyer-new',
    description: 'Präsentationen mit KI erzeugen', keywords: ['slides', 'präsentation', 'deck', 'folien', 'slide deck builder'],
  },
  {
    id: 'li-leadmagnets', recht: 'inhalte.lesen', path: '/lead-magnets', label: 'Lead-Magnete (Whitepaper, Checklisten)', shortLabel: 'Lead-Magnete', icon: 'magnet', parent: 'li-flyer-new', tabOf: 'li-flyer-new',
    description: 'Whitepaper, Checklisten und Co.', keywords: ['lead', 'magnet', 'lead magnets', 'whitepaper', 'checkliste'],
  },

  // --- Vertrieb ------------------------------------------------------------
  {
    id: 'sales-overview', recht: 'vertrieb.lesen', path: '/vertrieb', label: 'Cockpit', icon: 'target', group: 'sales',
    description: 'Vertrieb auf einen Blick: heute dran, Pipeline, Ablauf', keywords: ['vertrieb', 'cockpit', 'übersicht', 'crm'],
  },
  // Heute ist Reiter des Cockpits (Entscheidung 13): das Cockpit zeigt "Heute dran" ohnehin.
  {
    id: 'today', recht: 'vertrieb.lesen', path: '/aufgaben', label: 'Heute', icon: 'sun', parent: 'sales-overview', tabOf: 'sales-overview',
    description: 'Aufgaben und Termine des Tages', keywords: ['aufgaben', 'heute', 'todo'],
  },
  // Kampagnen: eine Sammelseite fuer alle vier Kampagnenarten (Reiter nach Zweck);
  // die ICP-, Signal- und Firmen-Mailing-Kampagnen stehen weiter unten bei den Werkzeugen.
  {
    id: 'campaigns', recht: 'vertrieb.lesen', path: '/sales-campaigns', label: 'Zielfirmen recherchieren (KI)', navLabel: 'Kampagnen', shortLabel: 'Kampagnen',
    icon: 'megaphone', group: 'sales', description: 'Kampagnen planen: Zielfirmen und Ansprechpartner per KI recherchieren',
    keywords: ['kampagne', 'vertrieb', 'vertriebskampagnen', 'sales campaigns'],
  },
  { id: 'campaigns-detail', recht: 'vertrieb.lesen', path: '/sales-campaigns/:id', label: 'Kampagne', parent: 'campaigns' },
  {
    id: 'companies', recht: 'vertrieb.lesen', path: '/companies', label: 'Firmen', icon: 'building', group: 'sales',
    description: 'Firmenakten', keywords: ['firma', 'unternehmen', 'akte'],
  },
  { id: 'companies-detail', recht: 'vertrieb.lesen', path: '/companies/:id', label: 'Firmenakte', parent: 'companies' },
  {
    id: 'duplicates', recht: 'vertrieb.lesen', path: '/duplicates', label: 'Duplikate', icon: 'copy', parent: 'companies', tabOf: 'companies',
    description: 'Doppelte Leads und Firmen finden und zusammenführen', keywords: ['duplikat', 'dublette', 'merge'],
  },
  {
    id: 'leads', recht: 'vertrieb.lesen', path: '/lead-sync', label: 'Leads & Outreach', icon: 'zap', group: 'sales',
    description: 'Leads importieren und anschreiben', keywords: ['leads', 'outreach', 'lemlist'],
  },
  {
    id: 'inbox', recht: 'vertrieb.lesen', path: '/outreach-inbox', label: 'Antworten', icon: 'inbox', group: 'sales',
    description: 'Eingehende Antworten bearbeiten', keywords: ['antworten', 'inbox', 'postfach'],
  },
  {
    id: 'pipeline', recht: 'vertrieb.lesen', path: '/pipeline', label: 'Pipeline', icon: 'kanban', group: 'sales',
    description: 'Vertriebs-Pipeline (Kanban)', keywords: ['pipeline', 'deals', 'vorgaenge'],
  },
  {
    id: 'quotes', recht: 'angebot.lesen', path: '/angebote', label: 'Angebote', icon: 'file-check', group: 'sales',
    description: 'Angebote erstellen und verfolgen', keywords: ['angebot', 'quote'],
  },
  {
    // Reiter von Angebote (Entscheidung 13: die Vertriebsgruppe bleibt bei sieben Eintraegen).
    id: 'contracts', recht: 'vertrieb.lesen', path: '/vertraege', label: 'Verträge', icon: 'file-text', parent: 'quotes', tabOf: 'quotes',
    description: 'Vertragsregister: Laufzeit, Kündigungsfrist, AVV und NDA', keywords: ['vertrag', 'verträge', 'kündigung', 'frist', 'avv', 'nda', 'geheimhaltung', 'rahmenvertrag', 'contract'],
  },
  // Postfaecher sind Reiter von Leads & Outreach, die Auswertung Reiter der Pipeline (Entscheidung 13).
  {
    id: 'mailboxes', recht: 'versand.lesen', path: '/mailboxes', label: 'Postfächer', icon: 'mailbox', parent: 'leads', tabOf: 'leads',
    description: 'Versandpostfächer und Warm-up', keywords: ['postfach', 'mailbox', 'warmup'],
  },
  {
    id: 'report', recht: 'vertrieb.lesen', path: '/auswertung', label: 'Auswertung', icon: 'pie-chart', parent: 'pipeline', tabOf: 'pipeline',
    description: 'Kampagnen-Auswertung', keywords: ['auswertung', 'statistik', 'report'],
  },

  // --- Vertrieb: Vorbereitung -- drei Sammelseiten, die Werkzeuge darin als Reiter --
  // Welle 2: Werkzeugseiten, die nur aus POST-Aufrufen bestehen (KI-Analyse,
  // Generieren), verlangen 'vertrieb.bearbeiten' -- das Backend prueft bei
  // jedem Nicht-GET das Schreibrecht des Routers. Buchhaltung (nur
  // 'vertrieb.lesen') sieht sie darum nicht; GTM-Uebersicht und -Setup lesen
  // per GET und bleiben auf 'vertrieb.lesen'. Der Reply Handler listet den
  // Posteingang per GET /replies/inbox und bleibt darum ebenfalls lesbar;
  // Analysieren und Erledigt blendet die Seite ohne Schreibrecht aus.
  // GTM & Strategie: Fundament, Nutzen, Zielgruppe, Recherche, Listen
  {
    id: 'gtm-overview', recht: 'vertrieb.lesen', path: '/gtm-overview', label: 'Markteintritt: Übersicht (GTM)', icon: 'eye', navLabel: 'Markteintritt (GTM)', group: 'sales-tools',
    description: 'Go-to-Market-Stand: ICP, Personas, Angebote, Kampagnen', keywords: ['gtm', 'übersicht', 'go-to-market', 'strategie', 'markteintritt'],
  },
  {
    id: 'gtm-setup', recht: 'vertrieb.lesen', path: '/gtm-setup', label: 'Zielkunden, Personas & Angebote (GTM-Setup)', shortLabel: 'GTM-Setup', icon: 'flag', parent: 'gtm-overview', tabOf: 'gtm-overview',
    description: 'ICP, Personas und Angebote pflegen', keywords: ['gtm', 'icp', 'persona', 'angebot', 'offer', 'setup', 'gtm setup'],
  },
  {
    id: 'value-props', recht: 'vertrieb.bearbeiten', path: '/value-props', label: 'Nutzenversprechen (Value Props)', shortLabel: 'Nutzen', icon: 'lightbulb', parent: 'gtm-overview', tabOf: 'gtm-overview',
    description: 'Nutzenversprechen je Persona und Pain', keywords: ['value', 'proposition', 'nutzen', 'usp', 'value prop', 'value proposition'],
  },
  {
    id: 'persona-insights', recht: 'vertrieb.bearbeiten', path: '/persona-insights', label: 'Persona-Erkenntnisse (Persona Insights)', shortLabel: 'Personas', icon: 'users', parent: 'gtm-overview', tabOf: 'gtm-overview',
    description: 'Erkenntnisse je Persona', keywords: ['persona', 'insights', 'zielgruppe', 'persona insights', 'erkenntnisse'],
  },
  {
    id: 'research', recht: 'vertrieb.bearbeiten', path: '/research', label: 'Marktrecherche (Sales Research)', shortLabel: 'Recherche', icon: 'search', parent: 'gtm-overview', tabOf: 'gtm-overview',
    description: 'Wettbewerb, Markt, Pain Points, Buying-Trigger', keywords: ['research', 'recherche', 'wettbewerb', 'markt', 'sales research', 'marktrecherche'],
  },
  {
    id: 'list-building', recht: 'vertrieb.bearbeiten', path: '/list-building', label: 'Zielfirmenlisten (Listbuilding)', shortLabel: 'Zielfirmenlisten', icon: 'layers', parent: 'gtm-overview', tabOf: 'gtm-overview',
    description: 'Zielgruppenlisten aufbauen', keywords: ['liste', 'listbuilding', 'leads', 'filter', 'list building', 'zielfirmen'],
  },
  // Ansprache & Texte: Kadenz, Copy, Icebreaker, Sequenzen, Antworten
  {
    id: 'campaign-architect', recht: 'vertrieb.bearbeiten', path: '/campaign-architect', label: 'Kampagnen-Architekt', icon: 'briefcase', navLabel: 'Ansprache & Texte', group: 'sales-tools',
    description: 'Kampagnen aus ICP und Angebot entwerfen', keywords: ['kampagne', 'architekt', 'planung', 'ansprache', 'texte', 'campaign architect'],
  },
  {
    id: 'copywriting-studio', recht: 'vertrieb.bearbeiten', path: '/copywriting-studio', label: 'Kaltakquise-Texte (Cold-Copy Studio)', shortLabel: 'Texte', icon: 'edit', parent: 'campaign-architect', tabOf: 'campaign-architect',
    description: 'Cold-E-Mail-Texte schreiben und bewerten', keywords: ['copy', 'copywriting', 'cold', 'email', 'text', 'cold copy', 'cold email', 'kaltakquise'],
  },
  {
    id: 'icebreaker', recht: 'vertrieb.bearbeiten', path: '/icebreaker', label: 'Einstiegssätze (Icebreaker)', shortLabel: 'Einstiegssätze', icon: 'sparkles', parent: 'campaign-architect', tabOf: 'campaign-architect',
    description: 'Personalisierte Einstiegssätze', keywords: ['icebreaker', 'personalisierung', 'opener', 'einstiegssätze'],
  },
  {
    id: 'sequences', recht: 'versand.lesen', path: '/sequences', label: 'Sequenzen', icon: 'workflow', parent: 'campaign-architect', tabOf: 'campaign-architect',
    description: 'Outreach-Sequenzen', keywords: ['sequenz', 'sequence', 'lemlist', 'schritte'],
  },
  {
    id: 'reply-handler', recht: 'vertrieb.lesen', path: '/reply-handler', label: 'Antworten einordnen (Reply Handler)', icon: 'mail', parent: 'campaign-architect', tabOf: 'campaign-architect',
    description: 'Antworten mit KI einordnen und beantworten', keywords: ['reply', 'antwort', 'einwand', 'reply handler'],
  },
  // Kampagnenarten: ICP-, Signal- und Firmen-Mailing-Kampagnen sind Reiter von
  // "Kampagnen" in der Gruppe Vertrieb (Paket 4 Schritt 1); `parent` bleibt fuer die
  // Brotkrumen, `tabOf` zeigt auf die Sammelseite (tabsFor sammelt nur eine Ebene).
  // Firmen-Mailing liegt im Backend in der Versand-Zeile (public-outreach), darum
  // 'versand.lesen' wie Postfaecher und Sequenzen.
  {
    id: 'icp-campaigns', recht: 'vertrieb.lesen', path: '/icp-campaigns', label: 'Leads nach Zielkundenprofil (ICP)', shortLabel: 'ICP', icon: 'target',
    parent: 'campaigns', tabOf: 'campaigns', description: 'Kampagnen je ICP', keywords: ['icp', 'kampagne', 'kampagnen', 'icp campaigns', 'icp-kampagnen', 'zielkundenprofil'],
  },
  { id: 'icp-campaigns-detail', recht: 'vertrieb.lesen', path: '/icp-campaigns/:id', label: 'ICP-Kampagne', parent: 'icp-campaigns' },
  {
    id: 'signal-campaigns', recht: 'vertrieb.lesen', path: '/signal-campaigns', label: 'Kaufsignale', icon: 'zap', parent: 'icp-campaigns', tabOf: 'campaigns',
    description: 'Kampagnen aus Kaufsignalen', keywords: ['signal', 'trigger', 'kampagne', 'signal campaigns', 'signal-kampagnen'],
  },
  { id: 'signal-campaigns-detail', recht: 'vertrieb.lesen', path: '/signal-campaigns/:id', label: 'Signal-Kampagne', parent: 'signal-campaigns' },
  {
    id: 'public-outreach', recht: 'versand.lesen', path: '/public-outreach', label: 'Firmen-Mailing', icon: 'send', parent: 'icp-campaigns', tabOf: 'campaigns',
    description: 'Mailing an öffentliche Firmenadressen', keywords: ['firmen', 'mailing', 'outreach', 'public outreach'],
  },
  { id: 'public-outreach-detail', recht: 'versand.lesen', path: '/public-outreach/:id', label: 'Firmen-Mailing-Kampagne', parent: 'public-outreach' },
  // Analysen: Outbound und Pipeline. Beide werten per POST aus; der
  // Lemlist-Modus liest /lemlist (Versand). Die A/B-Ergebnisse der
  // Signal-Kampagnen stehen lesend auf deren Detailseite.
  {
    id: 'outbound-analysis', recht: 'vertrieb.bearbeiten', path: '/outbound-analysis', label: 'Kampagnen-Kennzahlen (Outbound)', icon: 'bar-chart', navLabel: 'Analysen', group: 'sales-tools',
    description: 'Kennzahlen der Outbound-Kampagnen', keywords: ['outbound', 'analyse', 'kennzahlen', 'analysen', 'outbound analysis', 'outbound-analyse'],
  },
  {
    id: 'pipeline-analysis', recht: 'vertrieb.bearbeiten', path: '/pipeline-analysis', label: 'Pipeline-Auswertung (Forecast)', icon: 'pie-chart', parent: 'outbound-analysis', tabOf: 'outbound-analysis',
    description: 'Auswertung der Vertriebs-Pipeline', keywords: ['pipeline', 'analyse', 'forecast', 'pipeline analysis', 'pipeline-analyse'],
  },

  // --- ERP (ADR 0008): Cockpit, Auftrag -> Rechnung -> Zahlung, Einkauf, Buchhaltung -
  // Einstiegsseite mit der Kette Angebot -> Auftrag -> Rechnung -> Zahlung -> Mahnung -> Buchhaltung
  // (src/erp/erpSteps.ts). Leserechte je Bereich (Entscheidung 17): Auftraege und Lager
  // auch fuer den Vertrieb, Rechnungen, Zahlungen/Mahnwesen und Einkauf nicht; das
  // Cockpit haengt am Auftragsrecht, seine Karten pruefen ihr eigenes Recht. Pfad /erp kollidiert nicht mit /erp/einstellungen (exakter Treffer).
  {
    id: 'erp-cockpit', recht: 'erp.auftrag.lesen', path: '/erp', label: 'ERP-Cockpit', navLabel: 'Cockpit', shortLabel: 'ERP', icon: 'target', group: 'erp',
    description: 'Der ERP-Ablauf auf einen Blick: Angebot, Auftrag, Rechnung, Zahlung, Mahnung, Buchhaltung',
    keywords: ['erp', 'cockpit', 'überblick', 'ablauf', 'einstieg'],
  },
  {
    id: 'erp-orders', recht: 'erp.auftrag.lesen', path: '/auftraege', label: 'Aufträge', icon: 'file-check', group: 'erp',
    description: 'Aufträge aus Angeboten, Auftragsbestätigung, Lieferung', keywords: ['auftrag', 'auftragsbestätigung', 'bestellung kunde', 'order'],
  },
  {
    id: 'erp-invoices', recht: 'erp.rechnung.lesen', path: '/rechnungen', label: 'Rechnungen', icon: 'receipt', group: 'erp',
    description: 'Rechnungen und Gutschriften ausstellen, PDF, XRechnung, ZUGFeRD', keywords: ['rechnung', 'gutschrift', 'xrechnung', 'zugferd', 'invoice'],
  },
  {
    id: 'erp-payments', recht: 'erp.zahlung.lesen', path: '/zahlungen', label: 'Zahlungen & offene Posten', shortLabel: 'Zahlungen', icon: 'credit-card', group: 'erp',
    description: 'Zahlungen buchen, Bankumsätze (CAMT) zuordnen, SEPA-Dateien', keywords: ['zahlung', 'offene posten', 'bank', 'camt', 'sepa', 'lastschrift', 'überweisung'],
  },
  {
    id: 'erp-dunning', recht: 'erp.zahlung.lesen', path: '/mahnwesen', label: 'Mahnwesen', icon: 'alert', group: 'erp',
    description: 'Mahnläufe und Einzelmahnungen für überfällige Rechnungen', keywords: ['mahnung', 'mahnlauf', 'überfällig', 'verzug'],
  },
  {
    id: 'erp-tagesabrechnung', recht: 'erp.rechnung.lesen', path: '/tagesabrechnung', label: 'Tagesabrechnung', icon: 'clock', group: 'erp',
    description: 'Täglicher Lauf: Posten aller Produkte, gelieferte Aufträge und Projektzeiten werden zu Rechnungen', keywords: ['tagesabrechnung', 'abrechnung', 'posten', 'lauf', 'freigabe', 'debitor', 'stripe', 'core', 'snapscan'],
  },
  {
    id: 'erp-suppliers', recht: 'erp.einkauf.lesen', path: '/lieferanten', label: 'Lieferanten', icon: 'truck', group: 'erp',
    description: 'Lieferantenstamm (Kreditoren)', keywords: ['lieferant', 'kreditor', 'supplier'],
  },
  {
    id: 'erp-purchase-orders', recht: 'erp.einkauf.lesen', path: '/bestellungen', label: 'Bestellungen', icon: 'shopping-cart', group: 'erp',
    description: 'Bestellungen beim Lieferanten und Wareneingang', keywords: ['bestellung', 'einkauf', 'wareneingang', 'purchase order'],
  },
  {
    id: 'erp-purchase-invoices', recht: 'erp.einkauf.lesen', path: '/eingangsrechnungen', label: 'Eingangsrechnungen', icon: 'inbox', group: 'erp',
    description: 'Eingangsrechnungen erfassen, freigeben, bezahlen', keywords: ['eingangsrechnung', 'verbindlichkeiten', 'lieferantenrechnung'],
  },
  {
    id: 'erp-accounting', recht: 'buchhaltung.lesen', path: '/buchhaltung', label: 'Buchhaltung', icon: 'book-open', group: 'erp',
    description: 'Journal, Konten, UStVA, DATEV-Export, Festschreibung', keywords: ['buchhaltung', 'journal', 'konten', 'ustva', 'datev', 'festschreibung', 'skr03', 'skr04'],
  },
  {
    id: 'erp-stock', recht: 'erp.lager.lesen', path: '/lager', label: 'Artikel & Lager', shortLabel: 'Lager', icon: 'package', group: 'erp',
    description: 'Bestände, Lagerbewegungen, Inventur, Artikelstamm', keywords: ['lager', 'bestand', 'artikel', 'inventur', 'umbuchung'],
  },
  {
    id: 'erp-projects', recht: 'projekt.lesen', path: '/projekte', label: 'Projekte & Zeiten', shortLabel: 'Projekte', icon: 'briefcase', group: 'erp',
    description: 'Projekte mit Budget und Zeiterfassung, Zeiten abrechnen', keywords: ['projekt', 'zeiterfassung', 'zeiten', 'stunden', 'budget'],
  },
  {
    id: 'erp-settings', recht: 'erp.einstellungen.lesen', path: '/erp/einstellungen', label: 'ERP-Einstellungen', icon: 'settings', group: 'erp',
    description: 'Firmenstamm, Bank, Steuer, Mahnstufen, Nummernkreise', keywords: ['einstellungen', 'firmenstamm', 'iban', 'mahnstufen', 'nummernkreis', 'kontenrahmen'],
  },
  // Ersteinrichtung: ohne `group`, damit die Sidebar nicht waechst; ueber tabOf/parent
  // in Palette und Brotkrumen erreichbar. Der Assistent schreibt nur, darum
  // 'erp.einstellungen.bearbeiten' (Paket 5 Schritt 6), nicht das Leserecht der Einstellungen.
  {
    id: 'erp-einrichtung', recht: 'erp.einstellungen.bearbeiten', path: '/erp/einrichtung', label: 'Ersteinrichtung', icon: 'compass', parent: 'erp-settings', tabOf: 'erp-settings',
    description: 'Assistent für den Start: Firma, Steuer, Bank, Kontenrahmen und Zahlungsziel in fünf Schritten',
    keywords: ['ersteinrichtung', 'einrichtung', 'assistent', 'wizard', 'erster start', 'firmenstamm', 'steuernummer', 'kontenrahmen', 'skr03', 'skr04'],
  },

  // --- Verwaltung ----------------------------------------------------------
  {
    id: 'tenant', recht: 'admin.mandant', path: '/tenant-management', label: 'Mandant', icon: 'settings', group: 'admin',
    description: 'Mandanten-Einstellungen', keywords: ['mandant', 'tenant', 'organisation'],
  },
  {
    id: 'users', recht: 'admin.benutzer', path: '/user-management', label: 'Benutzer', icon: 'user', group: 'admin',
    description: 'Benutzer und Rollen', keywords: ['benutzer', 'user', 'rollen', 'einladen'],
  },
  {
    id: 'options', recht: 'admin.optionen', path: '/options', label: 'Optionen', icon: 'settings', group: 'admin',
    description: 'Mandantenweite Konfiguration', keywords: ['optionen', 'einstellungen', 'konfiguration'],
  },
  {
    // Plattform-Admin: Lemlist-Antworten ohne Mandant zuweisen (mandantenuebergreifend).
    id: 'reply-clearing', recht: 'admin.mandant.alle', path: '/antworten-clearing', label: 'Antworten-Clearing', icon: 'inbox', group: 'admin',
    description: 'Lemlist-Antworten ohne Mandant zuweisen', keywords: ['clearing', 'antworten', 'lemlist', 'zuordnen', 'mandant', 'reply'],
  },

  // --- Versteckt -----------------------------------------------------------
  { id: 'profile', path: '/profile', label: 'Profil', icon: 'user', keywords: ['profil', 'passwort', 'konto'] },
  // Ohne `recht` und ohne Sidebar-Gruppe (Mitarbeit bleibt bei hoechstens 22 Eintraegen):
  // jede angemeldete Person erreicht die Seite ueber die Kopfzeile (TopBar) und das Profil.
  {
    id: 'mein-profil', path: '/mein-profil', label: 'Mein Profil verbinden', icon: 'link', parent: 'profile',
    description: 'Eigenes LinkedIn-, X- oder Instagram-Konto verbinden oder trennen',
    keywords: ['mein profil', 'verbinden', 'linkedin', 'x', 'twitter', 'instagram', 'persönlich', 'trennen', 'team'],
  },
  { id: 'not-found', path: '*', label: 'Seite nicht gefunden' },
];

const byId = new Map(ROUTES.map((r) => [r.id, r]));

export function routeById(id: string): RouteEntry | undefined {
  return byId.get(id);
}

/** Exakter Treffer fuer einen Pfad (ohne den '*'-Fallback). */
export function findRoute(pathname: string): RouteEntry | undefined {
  return ROUTES.find((r) => r.path !== '*' && matchPath({ path: r.path, end: true }, pathname) !== null);
}

/**
 * Ist der Sidebar-Eintrag fuer diesen Pfad "aktiv"? '/' nur exakt, alle
 * anderen auch fuer Unterseiten ('/workflows' bei '/workflows/create').
 */
export function isRouteActive(entry: RouteEntry, pathname: string): boolean {
  if (entry.path === '*') return false;
  if (entry.path === '/') return pathname === '/';
  return matchPath({ path: entry.path, end: false }, pathname) !== null;
}

/** Elternkette (Wurzel zuerst) fuer die Brotkrumen. */
export function breadcrumbsFor(pathname: string): RouteEntry[] {
  const leaf = findRoute(pathname);
  if (!leaf) return [];
  const chain: RouteEntry[] = [leaf];
  let cursor = leaf;
  const seen = new Set<string>([leaf.id]);
  while (cursor.parent) {
    const parent = byId.get(cursor.parent);
    if (!parent || seen.has(parent.id)) break;
    chain.unshift(parent);
    seen.add(parent.id);
    cursor = parent;
  }
  return chain;
}

/**
 * Reiter eines Sidebar-Eintrags: der Eintrag selbst plus alle Seiten mit
 * `tabOf` auf ihn. Mit `user` fallen Reiter weg, deren Recht fehlt
 * (z. B. Sequenzen fuer Buchhaltung); ohne `user` bleiben alle.
 */
export function tabsFor(entryId: string, user?: SidebarUser | null): RouteEntry[] {
  const root = byId.get(entryId);
  if (!root) return [];
  const tabs = [root, ...ROUTES.filter((r) => r.tabOf === entryId)];
  return user === undefined ? tabs : tabs.filter((r) => darfSehen(user, r));
}

/** Gehoert `r` zur Familie von `entry`: der Eintrag selbst, ein Reiter oder eine Unterseite (ueber parent/tabOf)? */
function gehoertZu(r: RouteEntry, entry: RouteEntry): boolean {
  const gesehen = new Set<string>();
  let cursor: RouteEntry | undefined = r;
  while (cursor && !gesehen.has(cursor.id)) {
    if (cursor.id === entry.id || cursor.tabOf === entry.id) return true;
    gesehen.add(cursor.id);
    cursor = cursor.parent ? byId.get(cursor.parent) : undefined;
  }
  return false;
}

/**
 * Aktiv in der Seitenleiste: der Eintrag selbst oder einer seiner Reiter,
 * auch auf Unterseiten. Trifft eine Seite ausserhalb der Familie den Pfad
 * genau, gewinnt sie: /erp/einstellungen gehoert zu "ERP-Einstellungen",
 * nicht zum Cockpit /erp.
 */
export function isNavEntryActive(entry: RouteEntry, pathname: string): boolean {
  const aktiv = isRouteActive(entry, pathname) || ROUTES.some((r) => r.tabOf === entry.id && isRouteActive(r, pathname));
  if (!aktiv) return false;
  const genau = findRoute(pathname);
  return !genau || gehoertZu(genau, entry);
}

export function groupOf(entry: RouteEntry | undefined): NavGroup | undefined {
  if (!entry) return undefined;
  const chain = [entry];
  let cursor = entry;
  while (cursor.parent) {
    const parent = byId.get(cursor.parent);
    if (!parent) break;
    chain.push(parent);
    cursor = parent;
  }
  const withGroup = chain.find((e) => e.group);
  return withGroup ? NAV_GROUPS.find((g) => g.id === withGroup.group) : undefined;
}

export function documentTitleFor(pathname: string): string {
  const entry = findRoute(pathname);
  return entry ? `${entry.label} · Intermal` : 'Intermal';
}

/** Das, was die Sidebar von der angemeldeten Person braucht: ihre `rechte`. */
export type SidebarUser = RechteTraeger;

/** Darf die Person den Eintrag sehen? Eintraege ohne `recht` sieht jede:r, ohne Benutzer nur diese. */
export function darfSehen(user: SidebarUser | null | undefined, entry: RouteEntry): boolean {
  return !entry.recht || hatRecht(user, entry.recht);
}

/**
 * Sidebar-Gruppen mit ihren Eintraegen, gefiltert nach den Rechten der
 * Person; leere Gruppen fallen weg. Ohne Benutzer (null) bleiben nur
 * Eintraege ohne `recht`, also Start.
 */
export function sidebarGroups(user: SidebarUser | null | undefined): Array<{ group: NavGroup; items: RouteEntry[] }> {
  return NAV_GROUPS
    .map((group) => ({ group, items: ROUTES.filter((r) => r.group === group.id && darfSehen(user, r)) }))
    .filter((g) => g.items.length > 0);
}
